import React, { useMemo, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Modal,
  ModalContent,
  ModalHeader,
  cn,
  Button,
  ModalFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  ScrollArea,
  getTypeBadge,
  useModal,
} from "@racoon/ui";
import { RouteStatus, JobEvent, ConnectProto, WasteType } from "@racoon/api";
import { Namespace, TranslationKey, useT, useLang, type TFn } from "@racoon/i18n";
import { fromTimestamp, getEnumName, sortJobsByStep } from "@racoon/lib";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";
import { RouteTransferModalContent } from "./msw-route-list/RouteTransferModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import {
  useUpdateRouteWithJobsMutation,
  useForceCompleteRouteMutation,
  useCancelRouteMutation as useForceCancelRouteMutation,
  useListRoutesQuery,
  useGetRouteQuery,
  useLazyGetRouteQuery,
} from "~/shared/api/routes/route.grpc";
import { useCancelJobMutation, useDetachJobFromRouteMutation } from "~/shared/api/jobs/job.grpc";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useListDestinationLocationsQuery } from "~/shared/api/locations/locations.grpc";
import { useLazyGetTruckListLatestPositionsQuery } from "~/shared/api/truck/truck.grpc";

import { ArrowRight, ChevronDown, Globe } from "lucide-react";

import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useAddJobsToRouteModal } from "../hooks/useAddJobsToRouteModal";

import { ForceCompleteModal } from "./ForceCompleteModal";
import { RouteJobCard } from "./RouteJobCard";
import { LocationPhotosSlider } from "~/features/pickup-location-modal/components/location-photos-slider";
import { RouteStatusBadge } from "~/pages/dashboard/routes/components/RouteStatusBadge";
import { useJobCard } from "~/pages/dashboard/orders/ui/use-job-card";
import { MapWithGeofence, MapObject } from "~/features/map-view/ui/map-view";
import { realOrgId } from "~/features/map-view/model/owner-org";
import { buildStepPins } from "~/features/map-view/model/helpers/step-pin-items";
import { InfoType } from "~/features/info-panel/model/useInfoPanel";
import { IcPhotoUploaded, IcPlus } from "@racoon/icons";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { ShiftStatusBadge } from "../../autopark/components/ShiftStatusBadge";

interface RouteJobsModalContentProps {
  route: ConnectProto.Route;
  onClose: () => void;
  navigate?: (to: string) => void;
  onCloseModal?: () => void;
  isNavigationMode?: boolean;
}

export interface RouteModalTitleContentProps {
  route: ConnectProto.Route;
}

export const RouteModalTitleContent: React.FC<RouteModalTitleContentProps> = ({
  route: routeSnapshot,
}) => {
  const t = useT();
  const lang = useLang();
  const { activeCompany } = useAuthSession();

  // The modal is opened with a snapshot, so follow the cache to stay in sync.
  const { data: liveRoute } = useGetRouteQuery({
    id: BigInt(routeSnapshot.id),
    ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
  });
  const route = liveRoute ?? routeSnapshot;

  const { data: routesResponse } = useListRoutesQuery(
    {
      ownerOrganizationIds: activeCompany?.id ? [BigInt(activeCompany.id)] : [],
      driverOnShiftIds: route.driverOnShiftId ? [BigInt(route.driverOnShiftId)] : [],
      routeCategories: [route.routeCategory],
    },
    { skip: !route.driverOnShiftId },
  );

  const tripNumber = useMemo(() => {
    if (!routesResponse?.routes) return undefined;

    const sortedRoutes = [...routesResponse.routes].sort((a, b) => {
      const timeA =
        fromTimestamp(a.factStartAt)?.getTime() ?? fromTimestamp(a.planStartAt)?.getTime() ?? 0;
      const timeB =
        fromTimestamp(b.factStartAt)?.getTime() ?? fromTimestamp(b.planStartAt)?.getTime() ?? 0;
      return timeA - timeB;
    });

    const index = sortedRoutes.findIndex((r) => r.id === route.id);
    return index !== -1 ? index + 1 : undefined;
  }, [routesResponse?.routes, route.id]);

  const locale = String(lang).includes("en") ? "en-GB" : "ru-RU";

  const formatTimeRoute = (dateStr?: string | Date) => {
    if (!dateStr) return "--:--";
    return new Date(dateStr).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDate = (dateStr?: string | Date) => {
    if (!dateStr) return "--.--.--";

    const formattedDate = new Date(dateStr).toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return formattedDate.replace(" г.", "");
  };

  const jobs = route.jobs || [];
  const completedJobsCount = jobs.filter(
    (j) => j.status === ConnectProto.JobStatus.COMPLETED,
  ).length;

  return (
    <div className="flex items-center flex-1 gap-4 font-normal justify-between">
      <div className="flex items-center gap-4">
        <div className="flex flex-col">
          <div className="flex items-center font-semibold gap-2 text-lg !leading-none">
            <span>M{Number(route.id)}</span>
            <span>
              {" "}
              {t(Namespace.Routes, "trip")} {tripNumber !== undefined ? tripNumber : ""}
            </span>
          </div>
          <span className="text-xs text-text-secondary font-semibold">
            {t(Namespace.Infopanel, "actions.cardRoute" as TranslationKey).toUpperCase()}
          </span>
        </div>
        <RouteStatusBadge
          status={`${ConnectProto.RouteStatusSchema.values[route.status].name}` as RouteStatus}
          jobs={jobs}
          completedJobs={completedJobsCount}
        />
        <div>
          {Number(route?.reward) > 0 && (
            <span className="bg-brand-yellow text-sm !leading-none px-2 py-1 rounded-2xl">
              {Number(route?.reward)} {t(Namespace.Customers, "modal.addContract.currency")}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center bg-[#F8FAFC] border border-gray-200 rounded-lg text-sm divide-x divide-gray-200">
        <div className="flex items-center gap-2 px-3 py-1.5">
          <span className="text-gray-800 font-medium">
            {t(Namespace.Infopanel, "labels.routeStarted" as TranslationKey)}
          </span>
          <div className="flex gap-1.5 text-text-secondary font-medium">
            <span>{formatTimeRoute(fromTimestamp(route.factStartAt) || undefined)}</span>
            <span>
              {formatDate(fromTimestamp(route.factStartAt || route.planStartAt) || undefined)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5">
          <span className="text-gray-800 font-medium">
            {t(Namespace.Infopanel, "labels.routeEnded" as TranslationKey)}
          </span>
          <div className="flex gap-1.5 text-text-secondary font-medium">
            <span>{formatTimeRoute(fromTimestamp(route.factEndAt) || undefined)}</span>
            <span>{formatDate(fromTimestamp(route.factEndAt) || undefined)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

let activeContentCount = 0;

const rpcErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === "string" ? message : undefined;
};

const translateBackendError = (message: string, t: TFn): string => {
  const errorMap: Record<string, string> = {
    "The route cannot be empty": t(Namespace.Routes, "errRouteEmpty"),
    "is JOB_STATUS_COMPLETED and cannot be removed": t(Namespace.Routes, "errJobCompleted"),
    "is JOB_STATUS_CANCELLED and cannot be removed": t(Namespace.Routes, "errJobCancelled"),
    "is in status JOB_STATUS_COMPLETED and cannot be detached": t(
      Namespace.Routes,
      "errJobCompleted",
    ),
    "is in status JOB_STATUS_CANCELLED and cannot be detached": t(
      Namespace.Routes,
      "errJobCancelled",
    ),
    "cannot be detached from its route": t(Namespace.Routes, "errJobCannotDetach"),
    "is in status JOB_STATUS_COMPLETED, cannot unassign": t(Namespace.Routes, "errJobCompleted"),
    "is in status JOB_STATUS_CANCELLED, cannot unassign": t(Namespace.Routes, "errJobCancelled"),
    "cannot unassign from route": t(Namespace.Routes, "errJobCannotDetach"),
    "Cannot cancel the route because it contains COMPLETED jobs": t(
      Namespace.Routes,
      "errCannotCancelCompleted",
    ),
    "Cannot cancel the route while a job is in progress": t(
      Namespace.Routes,
      "errCannotCancelActive",
    ),
    "Your organization is not authorized": t(Namespace.Routes, "errUnauthorized"),
    "No permission to trigger events": t(Namespace.Routes, "errNoPermission"),
  };

  const entry = Object.entries(errorMap).find(([key]) => message.includes(key));

  if (entry) {
    const match = message.match(/job (\d+)/i);
    const prefix = match ? `${t(Namespace.Routes, "order")} ${match[1]} ` : "";
    return `${prefix}${entry[1]}`;
  }

  return message;
};

export const RouteJobsModalContent: React.FC<RouteJobsModalContentProps> = ({
  route,
  onClose,
  navigate: propNavigate,
  onCloseModal,
  isNavigationMode,
}) => {
  const open = true;
  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };
  const t = useT();
  const { openModal, closeModal } = useModal();
  const { openAddJobsToRoute } = useAddJobsToRouteModal();
  const handleTransferClick = useCallback(
    (jobId: bigint) => {
      const modalId = openModal({
        size: "2xl",
        width: { md: "min(1200px, 94vw)" },
        bodyClassName: "flex flex-col flex-1 !p-0 min-h-0",
        closeButtonTop: "top-4",
        body: (
          <RouteTransferModalContent
            sourceRoute={route}
            sourceSelectedJobIds={[jobId]}
            initialDate={fromTimestamp(route.planStartAt) || undefined}
            onClose={() => closeModal(modalId)}
            onSuccess={() => {
              onClose();
            }}
          />
        ),
      });
    },
    [route, openModal, closeModal, onClose],
  );

  const navigate = React.useMemo(() => {
    return (
      propNavigate ||
      ((to: string) => {
        window.location.href = to;
      })
    );
  }, [propNavigate]);

  React.useEffect(() => {
    activeContentCount++;
    return () => {
      activeContentCount--;
      setTimeout(() => {
        if (activeContentCount === 0) {
          onCloseModal?.();
        }
      }, 0);
    };
  }, [onCloseModal]);

  const { activeCompany } = useAuthSession();
  const { lang } = useLang() || { lang: "ru" };

  // --- Formatting Utilities ---
  const locale = String(lang).includes("en") ? "en-GB" : "ru-RU";

  const formatTimeRoute = useCallback(
    (dateStr?: string | Date) => {
      if (!dateStr) return "--:--";
      return new Date(dateStr).toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    [locale],
  );

  const formatDate = useCallback(
    (dateStr?: string | Date) => {
      if (!dateStr) return "--.--.--";

      const formattedDate = new Date(dateStr).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      return formattedDate.replace(" г.", "");
    },
    [locale],
  );
  // --- Queries & Mutations ---
  // route.ownerOrganizationId is an int64 (bigint) — use it directly instead of
  // round-tripping through Number(), which would truncate IDs > MAX_SAFE_INTEGER.
  const ownerOrgId = route.ownerOrganizationId;
  const { data: depotsResponse } = useListDestinationLocationsQuery(
    ownerOrgId
      ? {
          ownerOrganizationIds: [ownerOrgId],
          types: [
            ConnectProto.LocationType.DEPOT,
            ConnectProto.LocationType.DISPOSAL_SITE,
            ConnectProto.LocationType.GAS_STATION,
            ConnectProto.LocationType.PROCESSING_PLANT,
            ConnectProto.LocationType.SORTING_COMPLEX,
            ConnectProto.LocationType.TRANSFER_STATION,
          ],
        }
      : skipToken,
  );
  const depots = useMemo(() => depotsResponse?.locations ?? [], [depotsResponse]);
  const [getLatestTruckStatus, { data: truckStatus }] = useLazyGetTruckListLatestPositionsQuery();
  const [updateRouteWithJobs, { isLoading: isSaving }] = useUpdateRouteWithJobsMutation();
  const [forceCompleteRouteMutation, { isLoading: isCompleting }] = useForceCompleteRouteMutation();
  const [forceCancelRouteMutation, { isLoading: isCancelling }] = useForceCancelRouteMutation();
  const [cancelJobMutation, { isLoading: isCancelingJob }] = useCancelJobMutation();
  const [detachJobMutation, { isLoading: isDetachingJob }] = useDetachJobFromRouteMutation();

  // --- Local State ---
  const [selectedDepot, setSelectedDepot] = useState<{
    id: bigint;
    name: string;
    lat?: number;
    lon?: number;
    type?: ConnectProto.LocationType;
  } | null>(null);
  const [isDepotPopoverOpen, setIsDepotPopoverOpen] = useState(false);
  const [playerVisible, setPlayerVisible] = useState(false);
  const [isForceCompleteModalOpen, setIsForceCompleteModalOpen] = useState(false);
  const [isForceCancelModalOpen, setIsForceCancelModalOpen] = useState(false);
  const [localJobs, setLocalJobs] = useState<ConnectProto.Job[]>([]);
  const [originalJobs, setOriginalJobs] = useState<ConnectProto.Job[]>([]);
  const [triggerGetRoute] = useLazyGetRouteQuery();
  // Same snapshot problem as in the title: read the status from the cache.
  const { data: liveRoute } = useGetRouteQuery({
    id: BigInt(route.id),
    ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
  });
  const routeStatus = liveRoute?.status ?? route.status;
  // A TTN approval re-stamps the shift after the modal opened.
  const shift = liveRoute?.driverOnShift ?? route.driverOnShift;
  const [selectedJobId, setSelectedJobId] = useState<bigint | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const { openJob } = useJobCard();
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [customDialog, setCustomDialog] = useState<{
    isOpen: boolean;
    type: "confirm" | "alert";
    text: string;
    onConfirm?: () => void | Promise<void>;
  } | null>(null);

  const showRpcError = (
    error: unknown,
    fallbackKey: "errServerError" | "errOperationFailed",
    format: (message: string) => string,
  ) => {
    const message = rpcErrorMessage(error);
    const errorMsg = message ? translateBackendError(message, t) : t(Namespace.Routes, fallbackKey);
    setCustomDialog({ isOpen: true, type: "alert", text: format(errorMsg) });
  };

  // --- Derived State ---
  const isRouteFinished =
    routeStatus === ConnectProto.RouteStatus.COMPLETED ||
    routeStatus === ConnectProto.RouteStatus.CANCELLED;

  const selectedJob = useMemo(
    () => localJobs.find((j) => j.id === selectedJobId),
    [localJobs, selectedJobId],
  );
  const isSelectedJobActive =
    selectedJob &&
    (selectedJob.status === ConnectProto.JobStatus.ON_SERVICE ||
      selectedJob.status === ConnectProto.JobStatus.ENROUTE);

  const unfinishedJobs = useMemo(() => {
    return localJobs.filter(
      (job) =>
        job.status !== ConnectProto.JobStatus.COMPLETED &&
        job.status !== ConnectProto.JobStatus.CANCELLED,
    );
  }, [localJobs]);

  // Read from the cache, not localJobs: the snapshot can go stale while the modal is open.
  const routePhotos = useMemo(() => {
    return (
      ((liveRoute ?? route).jobs ?? [])
        .flatMap((job) => job.fileReports ?? [])
        // A report without a URL has nothing to show.
        .filter((file) => Boolean(file.url))
    );
  }, [liveRoute, route]);
  const routePhotosLabel = routePhotos.length
    ? t(Namespace.Routes, "viewPhoto")
    : t(Namespace.Routes, "noPhoto");

  const sortableJobIds = useMemo(() => {
    return localJobs
      .filter(
        (j) =>
          j.status === ConnectProto.JobStatus.ASSIGNED ||
          j.status === ConnectProto.JobStatus.CREATED,
      )
      .map((j) => String(j.id));
  }, [localJobs]);

  useEffect(() => {
    const sortedJobs = sortJobsByStep(route.jobs || []).map((job) =>
      job.driverOnShift ? job : { ...job, driverOnShift: route.driverOnShift },
    );
    setOriginalJobs(sortedJobs);
    setLocalJobs(sortedJobs);
  }, [route.jobs, route.driverOnShift]);

  useEffect(() => {
    if (route.destinationLocationId) {
      const depot = depots.find((d) => d.id === route.destinationLocationId);
      if (depot)
        setSelectedDepot({
          id: depot.id,
          name: depot.name,
          lat: depot.lat,
          lon: depot.lon,
          type: depot.type,
        });
    } else {
      setSelectedDepot(null);
    }
  }, [route.destinationLocationId, depots, open]);

  const pathSignature = useMemo(() => {
    return localJobs.map((j) => j.id).join("-") + "|" + (selectedDepot?.id || "none");
  }, [localJobs, selectedDepot]);
  const originalPathSignature = useMemo(() => {
    const sortedOriginal = sortJobsByStep(originalJobs);
    return (
      sortedOriginal.map((j) => j.id).join("-") + "|" + (route.destinationLocationId || "none")
    );
  }, [originalJobs, route.destinationLocationId]);

  const isRouteEdited = pathSignature !== originalPathSignature;

  const startAtDate = useMemo(() => fromTimestamp(route.planStartAt), [route.planStartAt]);
  const endAtDate = useMemo(() => fromTimestamp(route.planEndAt), [route.planEndAt]);

  const refreshRoute = useCallback(async () => {
    const updatedRoute = await triggerGetRoute({
      id: BigInt(route.id),
      ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
    }).unwrap();
    const sortedJobs = sortJobsByStep(updatedRoute.jobs ?? []).map((job) =>
      job.driverOnShift ? job : { ...job, driverOnShift: updatedRoute.driverOnShift },
    );
    setOriginalJobs(sortedJobs);
    setLocalJobs(sortedJobs);
    return updatedRoute;
  }, [route.id, activeCompany, triggerGetRoute]);

  const handleOpenAddJobsModal = useCallback(() => {
    if (!route?.id) return;

    openAddJobsToRoute({
      route,
      currentJobIds: localJobs.map((j) => j.id),
      initialDate: startAtDate ?? undefined,
      onAdded: async () => {
        try {
          await refreshRoute();
        } catch (error) {
          console.error("Failed to fetch updated route after adding jobs:", error);
        }
      },
    });
  }, [route, localJobs, startAtDate, openAddJobsToRoute, refreshRoute]);

  const durationText = useMemo(() => {
    if (!startAtDate || !endAtDate) return "";
    const diffMs = endAtDate.getTime() - startAtDate.getTime();
    if (diffMs <= 0) return "";

    const diffMins = Math.floor(diffMs / 1000 / 60);
    const hrs = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    const isEn = String(lang).includes("en");
    if (isEn) {
      return `${hrs > 0 ? `${hrs} h ` : ""}${mins} min`;
    }
    return `${hrs > 0 ? `${hrs} ч ` : ""}${mins} мин`;
  }, [startAtDate, endAtDate, lang]);

  const endTimeText = useMemo(() => {
    return formatTimeRoute(endAtDate || undefined);
  }, [endAtDate, formatTimeRoute]);

  const truckOwnerOrgId =
    realOrgId(route?.driverOnShift?.truck?.ownerOrganizationId) ??
    realOrgId(route?.ownerOrganizationId);

  useEffect(() => {
    if (!open || !route?.id || !truckOwnerOrgId) return;
    getLatestTruckStatus({ ownerOrganizationIds: [BigInt(truckOwnerOrgId)] });
  }, [open, route?.id, truckOwnerOrgId, getLatestTruckStatus]);

  // --- Drag and Drop Logic ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => setActiveDragId(String(event.active.id)),
    [],
  );
  const handleDragCancel = useCallback(() => setActiveDragId(null), []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeId = String(active.id);
      const overId = String(over.id);
      setLocalJobs((items) => {
        const overJob = items.find((j) => String(j.id) === overId);
        const isOverDraggable =
          overJob &&
          (overJob.status === ConnectProto.JobStatus.ASSIGNED ||
            overJob.status === ConnectProto.JobStatus.CREATED);

        if (!isOverDraggable) return items;

        const oldIndex = items.findIndex((item) => String(item.id) === activeId);
        const newIndex = items.findIndex((item) => String(item.id) === overId);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const activeDragJob = useMemo(
    () => localJobs.find((j) => String(j.id) === activeDragId),
    [activeDragId, localJobs],
  );

  // --- Core Operations ---
  const handleDepotSelect = (depot: (typeof depots)[number]) => {
    setSelectedDepot({
      id: depot.id,
      name: depot.name,
      lat: depot.lat,
      lon: depot.lon,
      type: depot.type,
    });
    setIsDepotPopoverOpen(false);
  };

  const handleScaleRecordClick = (recordId: string | number) => {
    const targetCompanyId = activeCompany?.id ?? route.ownerOrganizationId;

    if (targetCompanyId) {
      const timestamp = route?.scaleRecord?.bruttoScaleTs || route?.scaleRecord?.nettoScaleTs;
      const parsedStart = fromTimestamp(timestamp);
      const startDate = parsedStart ? parsedStart.toISOString() : "";
      const endDate = new Date().toISOString();

      let url = `/${targetCompanyId}/weight-management?highlightRecord=${recordId}`;

      if (startDate) {
        url += `&start=${startDate}`;
      }
      url += `&end=${endDate}`;
      navigate(url);
      onOpenChange(false);
    } else {
      console.error("Cannot navigate: No company ID found!");
    }
  };

  const routeUpdatePayload = useMemo(() => {
    const payload: {
      id: bigint;
      destinationLocationId?: bigint;
      destinationLat?: number;
      destinationLon?: number;
    } = { id: BigInt(route.id) };

    if (selectedDepot) {
      payload.destinationLocationId = selectedDepot.id;
      if (selectedDepot.lat != null && selectedDepot.lon != null) {
        payload.destinationLat = selectedDepot.lat;
        payload.destinationLon = selectedDepot.lon;
      }
    }
    return payload;
  }, [route.id, selectedDepot]);

  const getActiveJobIds = useCallback(
    (excludeId?: bigint) => {
      return localJobs.filter((j) => j.id !== excludeId).map((j) => j.id);
    },
    [localJobs],
  );

  const handleSaveRoute = async () => {
    try {
      await updateRouteWithJobs({
        route: routeUpdatePayload,
        jobIds: getActiveJobIds().map(BigInt),
        ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
      }).unwrap();
      onOpenChange(false);
    } catch (error: unknown) {
      showRpcError(
        error,
        "errServerError",
        (message) => `${t(Namespace.Routes, "error")}: ${message}`,
      );
    }
  };

  const handleDetachJobClick = (jobId: bigint) => {
    const emptiesStartedRoute =
      routeStatus !== ConnectProto.RouteStatus.CREATED &&
      routeStatus !== ConnectProto.RouteStatus.NOT_APPROVED &&
      localJobs.length === 1 &&
      localJobs[0].id === jobId;
    setCustomDialog({
      isOpen: true,
      type: "confirm",
      text: t(
        Namespace.Routes,
        emptiesStartedRoute ? "confirmRemoveLastOrder" : "confirmRemoveOrder",
      ),
      onConfirm: async () => {
        const job = localJobs.find((j) => j.id === jobId);
        if (!job) return;
        try {
          await detachJobMutation({
            id: job.id,
            serviceProviderId: job.serviceProviderId,
            serviceReceiverId: job.serviceReceiverId,
          }).unwrap();
          if (selectedJobId === jobId) setSelectedJobId(null);
          const updatedRoute = await refreshRoute();
          if (!updatedRoute.jobs?.length) onOpenChange(false);
        } catch (error: unknown) {
          showRpcError(
            error,
            "errServerError",
            (message) => `${t(Namespace.Routes, "errRemoveOrderFailed")}${message}`,
          );
        }
      },
    });
  };

  const handleCancelJob = async (jobId?: bigint) => {
    const targetJobId = jobId ?? selectedJobId;
    const job = localJobs.find((j) => j.id === targetJobId);
    if (!job) return;

    try {
      await cancelJobMutation({
        id: job.id,
        serviceProviderId: job.serviceProviderId,
        serviceReceiverId: job.serviceReceiverId,
      }).unwrap();
      setSelectedJobId(null);
      await refreshRoute();
    } catch (error: unknown) {
      showRpcError(
        error,
        "errOperationFailed",
        (message) => `${t(Namespace.Routes, "error")}: ${message}`,
      );
    }
  };

  const handleForceCancelClick = () => {
    if (unfinishedJobs.length > 0) {
      setIsForceCancelModalOpen(true);
    } else {
      handleConfirmForceCancel({});
    }
  };

  const handleConfirmForceCancel = async (resolutions: Record<number, JobEvent>) => {
    try {
      const jobsToCancel: bigint[] = [];
      const jobsToUnassign: bigint[] = [];
      Object.entries(resolutions).forEach(([jobIdStr, event]) => {
        const jobId = BigInt(jobIdStr);
        const eventVal = event as any;
        if (
          eventVal === JobEvent.JOB_EVENT_CANCEL ||
          eventVal === ConnectProto.JobEvent.CANCEL ||
          eventVal === "CANCEL" ||
          String(eventVal) === "CANCEL"
        ) {
          jobsToCancel.push(jobId);
        } else if (
          eventVal === JobEvent.JOB_EVENT_DETACH_FROM_ROUTE ||
          eventVal === ConnectProto.JobEvent.DETACH_FROM_ROUTE ||
          eventVal === "DETACH_FROM_ROUTE" ||
          String(eventVal) === "DETACH_FROM_ROUTE"
        ) {
          jobsToUnassign.push(jobId);
        }
      });

      await forceCancelRouteMutation({
        id: BigInt(route.id),
        jobsToCancel,
        jobsToUnassign,
        ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
      }).unwrap();
      setIsForceCancelModalOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error cancelling route:", error);
    }
  };

  const handleForceCompleteClick = () => {
    if (unfinishedJobs.length > 0) setIsForceCompleteModalOpen(true);
    else handleConfirmForceComplete({});
  };

  const handleConfirmForceComplete = async (resolutions: Record<number, JobEvent>) => {
    try {
      const jobsToComplete: bigint[] = [];
      const jobsToUnassign: bigint[] = [];
      Object.entries(resolutions).forEach(([jobIdStr, event]) => {
        const jobId = BigInt(jobIdStr);
        if (event === JobEvent.JOB_EVENT_FORCE_COMPLETE) {
          jobsToComplete.push(jobId);
        } else {
          jobsToUnassign.push(jobId);
        }
      });

      await forceCompleteRouteMutation({
        id: BigInt(route.id),
        jobsToComplete,
        jobsToUnassign,
        ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
      }).unwrap();
      setIsForceCompleteModalOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error completing route:", error);
    }
  };

  const handleHistoryClick = () => {
    if (activeCompany?.id && route?.driverOnShiftId && route.driverOnShift) {
      const startTime = route.factStartAt || route.planStartAt;
      const endTime = route.factEndAt || route.planEndAt;
      const shiftId = route.driverOnShiftId;
      const truckId = route.driverOnShift.truckId;

      let startDateParam = "";
      let endDateParam = "";
      let timeParam = "";

      if (startTime) {
        const startDate = fromTimestamp(startTime);
        if (startDate) {
          // The player starts here; midnight would hide the whole track.
          timeParam = `&startTime=${startDate.getTime()}`;
          startDate.setHours(0, 0, 0, 0);
          startDateParam = `&start=${startDate.toISOString()}`;
        }
      }
      if (endTime) {
        const endDate = fromTimestamp(endTime);
        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
          endDateParam = `&end=${endDate.toISOString()}`;
        }
      }

      navigate(
        `/${activeCompany.id}/gps-tracking?shiftId=${shiftId}&truckId=${truckId}${timeParam}${startDateParam}${endDateParam}`,
      );
      onOpenChange(false);
    }
  };

  // --- Map Objects Providers ---
  // Numbered in the list's (possibly unsaved, dragged) order.
  const mapObjects = useMemo<MapObject[]>(
    () => buildStepPins(localJobs.map((job, index) => ({ job, step: String(index + 1) }))),
    [localJobs],
  );

  const previewRoute = useMemo(() => {
    return {
      ...route,
      // Dragging reorders localJobs without touching stepIdx, and everything
      // downstream orders by stepIdx, so the unsaved order is restated here.
      jobs: localJobs.map((job, index) => ({ ...job, stepIdx: BigInt(index) })),
      destinationLocationId: selectedDepot?.id ?? undefined,
      destinationLat: selectedDepot?.lat,
      destinationLon: selectedDepot?.lon,
    };
  }, [route, localJobs, selectedDepot]);

  const routeTruckId = route.driverOnShift?.truck?.id;
  const realTruckGps = routeTruckId
    ? truckStatus?.truckPositions?.[String(routeTruckId)]
    : undefined;

  const mapTrucks = useMemo<MapObject[]>(() => {
    if (!routeTruckId) return [];

    const exactLat = realTruckGps?.latitude;
    const exactLon = realTruckGps?.longitude;
    const exactAngle = realTruckGps?.angle || 0;

    return [
      {
        id: Number(routeTruckId),
        name: route.driverOnShift?.truck?.plateNumber ?? "",
        type: "truck" as InfoType,
        lat: exactLat ?? (route.sourceLat || 43.238949),
        lon: exactLon ?? (route.sourceLon || 76.889709),
        angle: exactAngle,
        // resolveTruckSprite matches @racoon/api's string TruckType, while
        // ConnectProto's is numeric with the prefix stripped.
        truckType:
          route.driverOnShift?.truck?.type !== undefined
            ? `TRUCK_TYPE_${ConnectProto.TruckType[route.driverOnShift.truck.type]}`
            : "TRUCK_TYPE_MSW",
        ownerOrganizationId: route.driverOnShift?.truck?.ownerOrganizationId,
        _pathSignature: pathSignature,
      },
    ];
  }, [
    routeTruckId,
    realTruckGps?.latitude,
    realTruckGps?.longitude,
    realTruckGps?.angle,
    pathSignature,
    route.driverOnShift?.truck?.ownerOrganizationId,
    route.driverOnShift?.truck?.type,
    route.sourceLat,
    route.sourceLon,
  ]);

  const mapDepots = useMemo<MapObject[]>(() => {
    if (selectedDepot?.lat && selectedDepot?.lon) {
      return [
        {
          type: "depot" as InfoType,
          name: selectedDepot.name,
          locationType: selectedDepot.type,
          id: String(selectedDepot.id),
          lat: selectedDepot.lat,
          lon: selectedDepot.lon,
        } as MapObject,
      ];
    }
    return [];
  }, [selectedDepot]);

  return (
    <>
      {!isNavigationMode && (
        <ModalHeader className="p-4 !pr-12 !border-0">
          <RouteModalTitleContent route={route} />
        </ModalHeader>
      )}
      {/* Content Section: Lists & Map */}
      <div className="!h-[32rem] overflow-hidden flex flex-col lg:flex-row relative bg-white border-t">
        {/* Jobs List Panel */}
        <div className="w-full lg:w-[60%] flex flex-col !h-full bg-white">
          <div className="p-4">
            <div className="bg-text-invert rounded-xl h-[4.5rem] p-2 flex items-center justify-between ">
              <div className="flex items-center h-full gap-4">
                <div className="flex flex-col items-center h-full">
                  <div className="border border-black rounded-lg h-8 px-2 py-1 font-mono font-bold text-base leading-4 bg-white flex justify-center items-center">
                    {route.driverOnShift?.truck?.plateNumber || "---"}
                  </div>
                </div>
                <div className="text-[14px] my-2 gap-2 flex flex-col text-gray-900">
                  {route.driverOnShift?.driver?.firstName} {route.driverOnShift?.driver?.lastName}
                  <div className="flex items-center gap-4 text-[14px] text-text-secondary text-right">
                    <span>
                      {route.driverOnShift?.truck?.truckType?.model ||
                        route.driverOnShift?.truck?.model ||
                        "---"}
                    </span>
                    <span>{humanTruckType(route.driverOnShift?.truck?.type, t) || "---"}</span>
                    <span>{route.driverOnShift?.truck?.ownerOrganization?.name || "---"}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 text-[14px] text-gray-700 text-right">
                {shift && (
                  <>
                    <ShiftStatusBadge status={shift.status} ttnApproved={isTtnApproved(shift)} />
                    <div className="text-text-secondary text-xs">
                      {t(Namespace.Routes, "shiftId", { id: String(shift.id) })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col p-4 pt-0">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragCancel={handleDragCancel}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
            >
              <SortableContext items={sortableJobIds} strategy={verticalListSortingStrategy}>
                <div className="p-2 flex flex-col max-h-[24.5rem] overflow-y-auto overflow-x-hidden bg-text-invert rounded-xl flex-1 min-w-0">
                  <div className="space-y-4 flex flex-col min-w-0">
                    {localJobs.map((job, index) => (
                      <React.Fragment key={job.id}>
                        <RouteJobCard
                          job={job}
                          jobs={localJobs}
                          index={index}
                          onDetach={handleDetachJobClick}
                          onEdit={(job) => openJob(job, localJobs)}
                          isSaving={isSaving || isDetachingJob}
                          isSelected={selectedJobId === job.id}
                          onSelect={() =>
                            setSelectedJobId((prev) => (prev === job.id ? null : job.id))
                          }
                          onTransfer={handleTransferClick}
                          onCancel={handleCancelJob}
                          isCanceling={isCancelingJob}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                  {!isRouteFinished && (
                    <button
                      type="button"
                      onClick={handleOpenAddJobsModal}
                      className="flex justify-start items-center gap-1 p-2 hover:bg-brand-blue-light rounded-xl mt-2"
                    >
                      <IcPlus className="w-6 h-6 text-brand-blue" />
                      <span className="text-brand-blue text-sm">
                        {t(Namespace.Routes, "addSingleJob")}
                      </span>
                    </button>
                  )}
                </div>
              </SortableContext>
              {typeof document !== "undefined" &&
                createPortal(
                  <DragOverlay adjustScale={false} dropAnimation={null}>
                    {activeDragJob ? (
                      <div className="w-full  opacity-90 cursor-grabbing">
                        <RouteJobCard
                          job={activeDragJob}
                          index={localJobs.findIndex((j) => String(j.id) === activeDragId)}
                          onDetach={handleDetachJobClick}
                          isSaving={isSaving}
                          isOverlay={true}
                          onTransfer={handleTransferClick}
                        />
                      </div>
                    ) : null}
                  </DragOverlay>,
                  document.body,
                )}
            </DndContext>
          </div>
        </div>

        {/* Map Panel */}
        <div className="w-full lg:w-[40%] bg-white relative z-0 flex flex-col pr-4 pl-0 pt-4 pb-4">
          <div className="flex-1 relative rounded-xl overflow-hidden border border-gray-200  bg-white">
            <MapWithGeofence
              showPlayer
              onPlayerVisibleChange={setPlayerVisible}
              previewRoute={previewRoute}
              objects={mapObjects}
              trucks={mapTrucks}
              depots={mapDepots}
              onMarkerClick={() => {}}
              className="h-full w-full z-0"
            />
            {route.factEndAt === undefined && (
              <div className="absolute top-3 left-3 z-[400] bg-white border rounded-xl h-8 px-2 flex items-center gap-2">
                <span className="font-bold text-[#1E293B] text-[13px] tracking-wider leading-none">
                  {t(Namespace.Routes, "estimatedEndTimeAbbr")}
                </span>
                <div className="w-[1px] h-[2rem] bg-text-invert w-[2px]" />
                <div className="flex items-center gap-2.5 text-brand-blue font-medium text-[13px] leading-none">
                  <span>{endTimeText}</span>
                  {durationText && <span>{durationText}</span>}
                </div>
              </div>
            )}
            {/* The map root is `isolate`, so the player cannot be stacked above
                this button; they are moved apart instead. */}
            <div
              className={cn(
                // Clears the player, else the 17px attribution.
                "absolute right-2 z-[400]",
                playerVisible ? "bottom-[84px]" : "bottom-[25px]",
              )}
            >
              <Button
                onClick={handleHistoryClick}
                variant="opacityBlueWithoutOutline"
                className="h-8 gap-1 flex justify-center items-center !px-3"
              >
                <span>{t(Namespace.Routes, "showInMovementControl")}</span>
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Footer: Depot Selection & Scale Record */}
      <div className="mx-4 mb-4 flex flex-row items-center bg-[#F8FAFC] border border-gray-200 rounded-lg h-[48px] pr-3 text-[13px] overflow-hidden gap-2">
        {/* Landfill/Depot Selection */}
        <div className="shrink-0 flex border-r h-full items-center">
          <Popover
            open={isRouteFinished ? false : isDepotPopoverOpen}
            onOpenChange={isRouteFinished ? undefined : setIsDepotPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button
                disabled={isRouteFinished}
                className={cn(
                  "flex items-center !pl-4 gap-1.5 px-2 h-full text-sm rounded-md font-medium transition-colors outline-none",
                  !isRouteFinished
                    ? "hover:bg-gray-100 text-gray-900"
                    : "opacity-70 cursor-not-allowed text-gray-600",
                )}
              >
                {selectedDepot ? selectedDepot.name : t(Namespace.Routes, "selectLandfill")}
                {!isRouteFinished && <ChevronDown size={14} className="text-gray-500" />}
              </button>
            </PopoverTrigger>
            {!isRouteFinished && (
              <PopoverContent className="w-[200px] p-1">
                <ScrollArea className="h-40">
                  {/* `depots` stays unfiltered so an archived saved destination keeps its name. */}
                  {depots
                    .filter((d) => d.status === ConnectProto.LocationStatus.ACTIVE)
                    .map((d) => (
                      <div
                        key={String(d.id)}
                        onClick={() => handleDepotSelect(d)}
                        className="cursor-pointer hover:bg-gray-100 rounded-md p-2 text-sm transition-colors"
                      >
                        {d.name}
                      </div>
                    ))}
                </ScrollArea>
              </PopoverContent>
            )}
          </Popover>
        </div>
        {/* Route Photos & Scale Record Information */}
        {(route?.scaleRecord || routePhotos.length > 0) && (
          <div className="flex flex-row items-center flex-1 overflow-hidden">
            <div className="flex flex-row items-center gap-6 whitespace-nowrap overflow-x-auto no-scrollbar w-full">
              <div className="flex flex-row items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  disabled={!routePhotos.length}
                  className={cn(
                    "transition-colors flex items-center justify-center",
                    routePhotos.length
                      ? "cursor-pointer hover:opacity-70 text-[#0066FF]"
                      : "cursor-default text-gray-400",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPhotoModalOpen(true);
                  }}
                  title={routePhotosLabel}
                  aria-label={routePhotosLabel}
                >
                  <IcPhotoUploaded width={24} height={24} />
                </button>

                {route?.scaleRecord && (
                  <span className="font-medium text-gray-900">
                    {t(Namespace.Routes, "record")}
                    <span
                      className="border-b border-blue-600 pb-[1px] text-blue-600 cursor-pointer hover:text-blue-800 transition-colors ml-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleScaleRecordClick(Number(route?.scaleRecord?.id));
                      }}
                      title={t(Namespace.Routes, "goToWeightRecord")}
                    >
                      #{Number(route?.scaleRecord?.id)}
                    </span>
                  </span>
                )}
              </div>

              {route?.scaleRecord && (
                <>
                  <div className="flex flex-row items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{t(Namespace.Routes, "dateTime")}</span>
                    <span className="text-gray-900 font-medium">
                      {formatTimeRoute(
                        fromTimestamp(
                          route?.scaleRecord?.bruttoScaleTs || route?.scaleRecord?.nettoScaleTs,
                        ) || undefined,
                      )}{" "}
                      {formatDate(
                        fromTimestamp(
                          route?.scaleRecord?.bruttoScaleTs || route?.scaleRecord?.nettoScaleTs,
                        ) || undefined,
                      )}
                    </span>
                  </div>

                  <div className="flex flex-row items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{t(Namespace.Routes, "netWeight")}</span>
                    <span className="text-gray-900 font-medium">
                      {route?.scaleRecord?.nettoWeightKg ?? "---"} кг
                    </span>
                  </div>

                  <div className="flex flex-row items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{t(Namespace.Routes, "volume")}</span>
                    <span className="text-gray-900 font-medium">
                      {route?.scaleRecord?.volume ?? "---"} м3
                    </span>
                  </div>

                  <div className="flex flex-row items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{t(Namespace.Routes, "wasteType")}</span>
                    <span className="text-gray-900 font-medium">
                      {getTypeBadge(
                        (getEnumName(
                          ConnectProto.WasteTypeSchema,
                          Number(route?.scaleRecord?.wasteType),
                        ) as WasteType | undefined) ?? WasteType.WASTE_TYPE_UNDEFINED,
                        t,
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Operational Footer Actions */}
      {!isRouteFinished && (
        <ModalFooter className="border-t border-gray-200 bg-white p-4 flex justify-end items-center">
          <div className="flex gap-3">
            <Button
              onClick={handleForceCancelClick}
              className="!bg-[#FFC2BE] !text-[#FF3B30] hover:!bg-[#FFD1D1] !shadow-none !border-0 font-medium"
            >
              {t(Namespace.Routes, "cancelRoute")}
            </Button>
            {routeStatus !== ConnectProto.RouteStatus.CREATED && (
              <Button
                onClick={handleForceCompleteClick}
                className="!bg-[#C4FFB5] !text-[#0E892D] hover:!bg-[#D1F0D7] !shadow-none !border-0 font-medium"
              >
                {t(Namespace.Routes, "completeRoute")}
              </Button>
            )}
            <Button
              onClick={handleSaveRoute}
              disabled={isSelectedJobActive || !isRouteEdited}
              className="!bg-brand-blue-light !text-brand-blue hover:!bg-[#CCE0FF] !shadow-none !border-0 font-medium min-w-[120px]"
            >
              {isSaving && !isCancelingJob
                ? t(Namespace.Common, "saving")
                : t(Namespace.Common, "save")}
            </Button>
          </div>
        </ModalFooter>
      )}

      {/* Overlays / External Modals */}
      {isForceCompleteModalOpen && (
        <ForceCompleteModal
          open={isForceCompleteModalOpen}
          onOpenChange={setIsForceCompleteModalOpen}
          unfinishedJobs={unfinishedJobs}
          onConfirm={handleConfirmForceComplete}
          isLoading={isCompleting}
        />
      )}
      {isForceCancelModalOpen && (
        <ForceCompleteModal
          open={isForceCancelModalOpen}
          onOpenChange={setIsForceCancelModalOpen}
          mode="cancel"
          unfinishedJobs={unfinishedJobs}
          onConfirm={handleConfirmForceCancel}
          isLoading={isCancelling}
        />
      )}
      {isPhotoModalOpen && (
        <LocationPhotosSlider
          title={t(Namespace.PhotoReport, "photoReport")}
          open={isPhotoModalOpen}
          onOpenChange={setIsPhotoModalOpen}
          photos={routePhotos}
        />
      )}

      <Modal
        open={customDialog?.isOpen || false}
        onOpenChange={(isOpen) => !isOpen && setCustomDialog(null)}
      >
        <ModalContent className="max-w-[400px] p-6 bg-white rounded-[12px] shadow-xl border-0 !gap-0">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={18} className="text-gray-800" />
            <span className="font-semibold text-gray-900 text-[15px]">
              {typeof window !== "undefined" ? window.location.host : ""}
            </span>
          </div>

          <div className="text-gray-800 text-[15px] mb-8">{customDialog?.text}</div>

          <div className="flex justify-end gap-3">
            {customDialog?.type === "confirm" && (
              <Button
                onClick={() => setCustomDialog(null)}
                className="!bg-white !text-gray-800 border border-gray-300 hover:!bg-gray-50 shadow-sm font-medium px-4 h-9 rounded-md transition-colors"
              >
                {t(Namespace.Operators, "cancel" as TranslationKey)}
              </Button>
            )}
            <Button
              onClick={() => {
                const action = customDialog?.onConfirm;
                setCustomDialog(null);
                if (action) action();
              }}
              className="!bg-[#0066FF] !text-white hover:!bg-blue-600 shadow-sm font-medium px-4 h-9 rounded-md transition-colors"
            >
              OK
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
